import { useState } from "react";
import Seo from "./Seo";
import { api } from "@/api/client";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { EnvelopeSimple, User, Phone, MapPin, IdentificationCard, Buildings } from "@phosphor-icons/react";
import { BUSINESS, organizationJsonLd } from "./business";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/public/contact", form);
      toast.success("Thanks — we'll reply within 1–2 business days.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not send. Please email ${BUSINESS.email} instead.`);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Us — DueMate"
        description={`Contact DueMate (${BUSINESS.legalName}, Udyam-registered MSME). Email ${BUSINESS.email} or call ${BUSINESS.phoneDisplay}.`}
        jsonLd={organizationJsonLd()}
      />

      <section className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4361EE]">Contact Us</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-2">We&apos;re here to help.</h1>
        <p className="text-base text-[#4A4A4A] font-semibold mt-4 max-w-xl">
          Questions about pricing, features, or getting started? Send us a note and we&apos;ll reply within 1–2 business days.
        </p>

        <div className="grid lg:grid-cols-[1fr_2fr] gap-6 mt-10">
          {/* Registered business info + contact card */}
          <div className="space-y-4 h-fit">
            {/* Quick contact */}
            <div className="nb-card p-6 space-y-5">
              <div>
                <div className="w-10 h-10 bg-[#06D6A0] border-2 border-black rounded-md flex items-center justify-center mb-3">
                  <EnvelopeSimple size={20} weight="bold" />
                </div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Email</p>
                <a
                  href={`mailto:${BUSINESS.email}`}
                  data-testid="business-email"
                  className="font-heading text-lg font-black hover:underline break-all"
                  itemProp="email"
                >
                  {BUSINESS.email}
                </a>
              </div>
              <div>
                <div className="w-10 h-10 bg-[#4361EE] border-2 border-black rounded-md flex items-center justify-center mb-3">
                  <Phone size={20} weight="bold" color="#FFFFFF" />
                </div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Phone</p>
                <a
                  href={`tel:${BUSINESS.phone}`}
                  data-testid="business-phone"
                  className="font-heading text-lg font-black hover:underline"
                  itemProp="telephone"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </div>
              <div>
                <div className="w-10 h-10 bg-[#FFD166] border-2 border-black rounded-md flex items-center justify-center mb-3">
                  <MapPin size={20} weight="bold" />
                </div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Registered Address</p>
                <address className="not-italic font-heading text-base font-black leading-snug" data-testid="business-address">
                  {BUSINESS.address.line1},<br />
                  {BUSINESS.address.locality}, {BUSINESS.address.region} {BUSINESS.address.postalCode},<br />
                  {BUSINESS.address.countryName}
                </address>
              </div>
            </div>

            {/* Registered business identity — visible for Meta / Google verifiers */}
            <div
              className="nb-card p-6 space-y-4 bg-[#FFD166]/40"
              data-testid="registered-business-info"
              itemScope
              itemType="https://schema.org/Organization"
            >
              <div className="flex items-center gap-2">
                <Buildings size={18} weight="bold" />
                <p className="text-xs font-bold tracking-[0.2em] uppercase">Registered Business</p>
              </div>
              <dl className="space-y-2.5 text-sm">
                <div>
                  <dt className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Legal name</dt>
                  <dd className="font-heading text-lg font-black leading-tight" itemProp="legalName">{BUSINESS.legalName}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Proprietor</dt>
                  <dd className="font-semibold" itemProp="founder">{BUSINESS.ownerName}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Organization type</dt>
                  <dd className="font-semibold">{BUSINESS.organizationType}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A] flex items-center gap-1">
                    <IdentificationCard size={12} weight="bold" /> Udyam Registration
                  </dt>
                  <dd className="font-mono text-sm font-bold" data-testid="udyam-number">{BUSINESS.udyamNumber}</dd>
                </div>
                <div>
                  <dt className="text-[10px] font-bold tracking-[0.15em] uppercase text-[#4A4A4A]">Registered since</dt>
                  <dd className="font-semibold">{BUSINESS.foundedDisplay}</dd>
                </div>
              </dl>
              <p className="text-[11px] font-semibold text-[#4A4A4A] pt-3 border-t-2 border-dashed border-black/15">
                Registered under the Ministry of MSME, Government of India (Udyam).
              </p>
            </div>
          </div>

          {/* Contact form */}
          <form onSubmit={submit} className="nb-card p-6 space-y-4 h-fit">
            <h2 className="font-heading text-2xl font-black">Send us a message</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Your name *</Label>
                <Input data-testid="contact-name" className="nb-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Email *</Label>
                <Input data-testid="contact-email" type="email" className="nb-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Subject</Label>
              <Input data-testid="contact-subject" className="nb-input" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Pricing / feature question / bug report..." />
            </div>
            <div>
              <Label className="text-xs font-bold tracking-[0.15em] uppercase mb-1.5 block">Message *</Label>
              <Textarea data-testid="contact-message" className="nb-input min-h-[140px]" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} maxLength={4000} />
            </div>
            <button data-testid="contact-submit" type="submit" disabled={sending} className="nb-btn bg-[#4361EE] text-white px-6 py-3">
              {sending ? "Sending…" : "Send Message"}
            </button>
            <p className="text-[11px] font-semibold text-[#4A4A4A]">
              We respond to every message. For urgent WhatsApp API or billing issues, please email us directly for the fastest response.
            </p>
          </form>
        </div>
      </section>
    </>
  );
}
